import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, X } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { t } from "@/lib/translations";
import { playSfx } from "@/lib/sounds";

export default function ParentGate({ open, onClose, onPass }) {
  const { language, soundOn } = useGame();
  const [shake, setShake] = useState(false);

  // Random question each open
  const { a, b, answer, choices } = useMemo(() => {
    if (!open) return { a: 3, b: 4, answer: 12, choices: [7, 12, 9, 15] };
    const a = 3 + Math.floor(Math.random() * 4);
    const b = 3 + Math.floor(Math.random() * 4);
    const ans = a * b;
    const set = new Set([ans]);
    while (set.size < 4) set.add(ans + Math.floor(Math.random() * 9 - 4) || ans + 1);
    const choices = Array.from(set).sort(() => Math.random() - 0.5);
    return { a, b, answer: ans, choices };
  }, [open]);

  const handleChoice = (c) => {
    if (c === answer) {
      playSfx("success", soundOn);
      onPass();
    } else {
      playSfx("wrong", soundOn);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={onClose}
          data-testid="parent-gate-overlay"
        >
          <motion.div
            initial={{ scale: 0.7, y: 30 }}
            animate={{ scale: 1, y: 0, x: shake ? [0, -8, 8, -8, 0] : 0 }}
            exit={{ scale: 0.7, y: 30 }}
            transition={{ type: "spring", damping: 18 }}
            className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full relative grain"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
              data-testid="parent-gate-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-amber-600" strokeWidth={3} />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2 text-slate-800">
                {t("parent_gate_title", language)}
              </h2>
              <p className="text-slate-500 mb-1 text-sm">{t("parent_gate_hint", language)}</p>
              <p className="font-display text-3xl sm:text-4xl font-bold my-4 text-sky-500">
                {a} × {b} = ?
              </p>
              <div className="grid grid-cols-2 gap-3 w-full mt-2">
                {choices.map((c) => (
                  <button
                    key={c}
                    onClick={() => handleChoice(c)}
                    className="toy-btn py-4 rounded-2xl bg-sky-400 hover:bg-sky-500 text-white font-display text-2xl font-bold shadow-[0_5px_0_#0284c7]"
                    data-testid={`parent-gate-choice-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
